/**
 * CivilX TMS experiments — deterministic, session-sticky A/B variant assignment.
 *
 * Design notes:
 *   - No backend assignment endpoint — we hash the telemetry session_id so the
 *     variant stays stable across the whole tab session AND we don't need a
 *     network round-trip on page load.
 *   - Variants are equal-weighted; for unequal splits, change the `pickVariant`
 *     logic to slice the [0..1) space accordingly.
 *   - Each experiment is namespaced by `experimentKey` so we can run multiple
 *     experiments concurrently against the same session.
 */

const SESSION_KEY = 'civilx_tms_telemetry_session';

// FNV-1a 32-bit — small, deterministic, good distribution for short strings.
const hashStr = (s) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
};

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // Mirror lib/telemetry.js fallback so we never desync.
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch { return `anon_${Math.random()}`; }
};

const EXPERIMENTS = {
  'demo-cta': {
    variants: ['A', 'B'],
    copy: {
      A: { label: 'Try live demo', helper: 'Sandboxed company, full data, 24h reset.' },
      B: { label: 'See it live → 30 seconds', helper: 'No signup. Full sandbox. Resets every 24h.' },
    },
  },
  'pricing-headline': {
    variants: ['A', 'B'],
    copy: {
      A: { label: 'Pick a plan. Scale on your terms.', helper: 'Cancel anytime. All plans include multi-tenant isolation and end-to-end JWT auth.' },
      B: { label: 'Simple pricing. No surprises.', helper: 'All plans cancel anytime. Multi-tenant isolation + JWT auth standard.' },
    },
  },
};

/**
 * pickVariant — returns the variant string ('A' | 'B' | ...) deterministically
 * from (experimentKey, session_id).
 */
export const pickVariant = (experimentKey) => {
  const exp = EXPERIMENTS[experimentKey];
  if (!exp) return null;
  const h = hashStr(`${experimentKey}|${getSessionId()}`);
  const bucket = (h % 1000) / 1000;          // [0..1)
  const slot = Math.floor(bucket * exp.variants.length);
  return exp.variants[Math.min(slot, exp.variants.length - 1)];
};

/**
 * getCopy — convenience wrapper that returns the variant copy block.
 */
export const getCopy = (experimentKey) => {
  const v = pickVariant(experimentKey);
  const exp = EXPERIMENTS[experimentKey];
  if (!v || !exp) return null;
  return { variant: v, ...exp.copy[v] };
};
